

import { createPortal } from "react-dom";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { useEffect, useState, useMemo, useRef } from "react";
import { SearchRow } from "./SearchRow";
import { useSearch, SearchItem } from "./hooks/useSearch";
import { useHandleOps } from "./hooks/useHandleOps";

type PSearchProps = {
  setIsSearchPopupOpenAction: (value: boolean) => void;
  popupRef: React.RefObject<HTMLDivElement | null>;
};

export const PSearch: React.FC<PSearchProps> = ({
  setIsSearchPopupOpenAction,
  popupRef,
}) => {
  const t = useTranslations("popup-search");
  const {
    searchState,
    setSearchState,
    searchResults,
    lockedEvent,
    lockEvent,
    unlockEvent,
    isAtPicker,
    parsedQuery,
    ghostText,
    atGhostText,
  } = useSearch();

  const { inputRef, selectedIndex, navigateToItem } = useHandleOps(
    searchResults,
    () => setIsSearchPopupOpenAction(false),
    lockedEvent,
    lockEvent,
    unlockEvent,
    searchState,
    setSearchState,
    ghostText,
    atGhostText,
  );

  const [mounted, setMounted] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Auto-scroll to keep selected item visible
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const selectedEl = container.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedEl) selectedEl.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Flat scored list during active search; grouped by category when browsing
  // Always group in @-picker mode so the "Events" section header is visible
  const showFlat = parsedQuery.pageQuery.length > 0 && !isAtPicker;

  const flatResults = useMemo(
    (): (SearchItem & { globalIndex: number })[] =>
      searchResults.map((item, i) => ({ ...item, globalIndex: i })),
    [searchResults],
  );

  const groupedResults = useMemo(() => {
    const groups: {
      category: string;
      items: (SearchItem & { globalIndex: number })[];
    }[] = [];
    let idx = 0;
    for (const item of searchResults) {
      let g = groups.find((g) => g.category === item.category);
      if (!g) {
        g = { category: item.category, items: [] };
        groups.push(g);
      }
      g.items.push({ ...item, globalIndex: idx++ });
    }
    return groups;
  }, [searchResults]);

  // Tab hint: lock event > @-complete > page-complete > nothing
  const highlightedItem = searchResults[selectedIndex];
  const showTabLock = !lockedEvent && !!highlightedItem?.eventId;
  const activeGhostText = atGhostText || ghostText;
  const showTabComplete = !showTabLock && !!activeGhostText;

  if (!mounted) return null;

  const portalContent = (
    <motion.div
      data-testid="psearch-dialog"
      className="fixed inset-0 bg-black/40 flex items-start justify-center z-10000 pt-[13dvh] sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsSearchPopupOpenAction(false);
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12 }}
    >
      <motion.div
        ref={popupRef}
        className="w-160 max-w-[92vw] surface-primary rounded-2xl shadow-2xl border outline-quaternary overflow-hidden"
        initial={{ opacity: 0, scale: 0.97, y: -8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: -8 }}
        transition={{ type: "spring", damping: 30, stiffness: 450 }}
      >
        {/* ── Search bar ─────────────────────────────────────────────── */}
        <div className="px-4 pt-3.5 pb-3 border-b outline-quaternary">
          <div className="flex items-center gap-2">

            {/* Locked-event pill */}
            {lockedEvent && (
              <div
                className={`flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg body-3-medium whitespace-nowrap shrink-0 ${
                  lockedEvent.archived
                    ? "hint-orange-bg hint-orange-font border hint-orange-outline"
                    : "surface-tertiary font-secondary"
                }`}
              >
                <span>{lockedEvent.name}</span>
                <button
                  onClick={unlockEvent}
                  className="hover:font-primary transition-colors"
                  tabIndex={-1}
                >
                  <IconX size={13} />
                </button>
              </div>
            )}

            {/* Input + ghost text */}
            <div className="relative flex-1 flex items-center min-w-0 h-8">
              {/* Search icon — only shown when not locked */}
              {!lockedEvent && (
                <IconSearch
                  size={16}
                  className="absolute left-0 font-tertiary pointer-events-none shrink-0"
                />
              )}

              {/* Ghost text overlay — sits behind cursor, aligned to input text */}
              {activeGhostText && (
                <div
                  className="absolute inset-0 flex items-center pointer-events-none overflow-hidden"
                  aria-hidden
                  style={{ paddingLeft: lockedEvent ? 0 : 24 }}
                >
                  {/* Invisible spacer matching the typed text width */}
                  <span className="body-2-regular invisible whitespace-pre select-none">
                    {searchState}
                  </span>
                  {/* Ghost suffix */}
                  <span className="body-2-regular font-tertiary opacity-40 whitespace-pre select-none">
                    {activeGhostText}
                  </span>
                </div>
              )}

              <input
                ref={inputRef}
                data-testid="psearch-input"
                value={searchState}
                onChange={(e) => setSearchState(e.target.value)}
                placeholder={lockedEvent ? t("searchPages") : t("search")}
                className={`w-full h-full body-2-regular font-primary bg-transparent focus:outline-none placeholder:font-tertiary placeholder:body-2-regular ${
                  !lockedEvent ? "pl-6" : ""
                } pr-1`}
                style={{ fontFamily: "inherit" }}
              />
            </div>
          </div>

        </div>

        {/* ── Results ────────────────────────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          className="max-h-100 overflow-y-auto p-2"
        >
          {searchResults.length === 0 ? (
            <div className="py-14 text-center body-2-regular font-tertiary">
              {t("noResults")}
            </div>
          ) : showFlat ? (
            /* Flat score-sorted list during active search */
            <div className="space-y-px">
              {flatResults.map((item) => (
                <div key={item.globalIndex} data-index={item.globalIndex}>
                  <SearchRow
                    icon={item.icon}
                    text={item.label}
                    subtitle={item.breadcrumb ?? item.category}
                    isSelected={item.globalIndex === selectedIndex}
                    archived={item.archived}
                    href={
                      item.action || (item.eventId && !lockedEvent)
                        ? undefined
                        : item.url
                    }
                    onClick={() => {
                      if (item.eventId && !lockedEvent) {
                        lockEvent(item.eventId);
                      } else {
                        navigateToItem(item);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            /* Grouped by category when browsing */
            <div className="space-y-1">
              {groupedResults.map((group) => (
                <div key={group.category}>
                  <p className="px-3 pt-1.5 pb-0.5 body-3-medium font-secondary">
                    {group.category}
                  </p>
                  <div className="space-y-px">
                    {group.items.map((item) => (
                      <div key={item.globalIndex} data-index={item.globalIndex}>
                        <SearchRow
                          icon={item.icon}
                          text={item.label}
                          subtitle={item.breadcrumb}
                          isSelected={item.globalIndex === selectedIndex}
                          archived={item.archived}
                          href={
                            item.action || (item.eventId && !lockedEvent)
                              ? undefined
                              : item.url
                          }
                          onClick={() => {
                            if (item.eventId && !lockedEvent) {
                              lockEvent(item.eventId);
                            } else {
                              navigateToItem(item);
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Keyboard hints — desktop-only: they describe physical-keyboard
            affordances and the row doesn't fit a 375px popup anyway. ── */}
        <div className="px-4 py-2.5 hidden sm:flex items-center gap-3.5 body-3-regular font-tertiary">
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded-md surface-secondary border outline-quaternary body-3-medium leading-none">
              ↑↓
            </kbd>
            {t("navigate")}
          </span>

          {(showTabLock || showTabComplete) && (
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded-md surface-secondary border outline-quaternary body-3-medium leading-none">
                tab
              </kbd>
              {showTabComplete ? t("complete") : t("select")}
            </span>
          )}

          {lockedEvent && (
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 rounded-md surface-secondary border outline-quaternary body-3-medium leading-none">
                ⌫
              </kbd>
              {t("back")}
            </span>
          )}

          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded-md surface-secondary border outline-quaternary body-3-medium leading-none">
              ↵
            </kbd>
            {t("open")}
          </span>

          <span className="flex items-center gap-1.5 ml-auto">
            <kbd className="px-1.5 py-0.5 rounded-md surface-secondary border outline-quaternary body-3-medium leading-none">
              esc
            </kbd>
            {t("close")}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );

  return createPortal(portalContent, document.body);
};
